<template>
  <div class="kpi-card" :class="`kpi-${variant}`">
    <div class="kpi-icon-wrap">
      <CIcon :name="icon" class="kpi-icon" size="xl" />
    </div>
    <div class="kpi-body">
      <div class="kpi-label">{{ label }}</div>
      <div class="kpi-value">{{ formattedValue }}</div>
      <div v-if="hasDelta" class="kpi-delta" :class="deltaClass">
        <CIcon v-if="deltaDirection === 'up'" name="cil-arrow-top" size="sm" />
        <CIcon v-else-if="deltaDirection === 'down'" name="cil-arrow-bottom" size="sm" />
        <CIcon v-else name="cil-circle" size="sm" />
        <span class="kpi-delta-value">{{ formattedDelta }}</span>
        <span v-if="deltaSuffix" class="kpi-delta-suffix">{{ deltaSuffix }}</span>
      </div>
      <div v-if="sublabel" class="kpi-sublabel">{{ sublabel }}</div>
    </div>
  </div>
</template>

<script>
export default {
  name: "WidgetsDashboardKpi",
  props: {
    label: { type: String, required: true },
    value: { type: [Number, String], default: 0 },
    icon: { type: String, default: "cil-chart-pie" },
    // Visual accent: 'primary' (blue), 'success' (green), 'warning' (amber),
    // 'info' (purple), 'neutral' (slate).
    variant: { type: String, default: "primary" },
    // Optional comparison number (delta = value - delta).
    delta: { type: Number, default: null },
    // e.g. "vs Apr" or "vs 2025"
    deltaSuffix: { type: String, default: "" },
    // Extra context line below the value (e.g. month name, range).
    sublabel: { type: String, default: "" },
  },
  computed: {
    formattedValue() {
      if (typeof this.value === "number") {
        return new Intl.NumberFormat("en-MY").format(this.value);
      }
      return this.value;
    },
    hasDelta() {
      return this.delta !== null && this.delta !== undefined;
    },
    deltaDirection() {
      if (!this.hasDelta) return "flat";
      if (this.delta > 0) return "up";
      if (this.delta < 0) return "down";
      return "flat";
    },
    deltaClass() {
      return `kpi-delta-${this.deltaDirection}`;
    },
    formattedDelta() {
      if (!this.hasDelta) return "";
      const abs = Math.abs(this.delta);
      return new Intl.NumberFormat("en-MY").format(abs);
    },
  },
};
</script>

<style scoped>
.kpi-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  border: 1px solid #f1f3f5;
  height: 100%;
  transition: box-shadow 0.2s ease;
}
.kpi-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.kpi-icon-wrap {
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: var(--kpi-bg, #eff6ff);
}
.kpi-icon {
  font-size: 1.4rem;
  color: var(--kpi-color, #2563eb);
}

.kpi-primary .kpi-icon-wrap { --kpi-bg: #eff6ff; }
.kpi-primary .kpi-icon { --kpi-color: #2563eb; }
.kpi-success .kpi-icon-wrap { --kpi-bg: #ecfdf5; }
.kpi-success .kpi-icon { --kpi-color: #059669; }
.kpi-warning .kpi-icon-wrap { --kpi-bg: #fffbeb; }
.kpi-warning .kpi-icon { --kpi-color: #d97706; }
.kpi-info .kpi-icon-wrap { --kpi-bg: #f3f0ff; }
.kpi-info .kpi-icon { --kpi-color: #7c3aed; }
.kpi-neutral .kpi-icon-wrap { --kpi-bg: #f3f4f6; }
.kpi-neutral .kpi-icon { --kpi-color: #4b5563; }

.kpi-body { min-width: 0; flex: 1; }
.kpi-label {
  font-size: 0.78rem;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
}
.kpi-value {
  font-size: 2rem;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.02em;
  line-height: 1.1;
}
.kpi-sublabel {
  margin-top: 4px;
  font-size: 0.78rem;
  color: #9ca3af;
}

.kpi-delta {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
}
.kpi-delta-up {
  background: #ecfdf5;
  color: #047857;
}
.kpi-delta-down {
  background: #fef2f2;
  color: #b91c1c;
}
.kpi-delta-flat {
  background: #f3f4f6;
  color: #6b7280;
}
.kpi-delta-suffix {
  margin-left: 2px;
  font-weight: 500;
  opacity: 0.85;
}

@media (max-width: 575.98px) {
  .kpi-card {
    padding: 14px;
    gap: 12px;
  }
  .kpi-icon-wrap {
    width: 48px;
    height: 48px;
  }
  .kpi-value {
    font-size: 1.6rem;
  }
}
</style>
